import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuote } from "@/components/QuoteProvider";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";
import { DIVISIONS } from "@/constants/site";
import {
  ArrowRight, CheckCircle2, Phone, MessageCircle, Camera, Wrench, Hash, Ruler, Package, Building2, Disc3,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1769147339214-076740872485?w=1600&auto=format&fit=crop";

const ENQUIRY_REQS = [
  { icon: Hash, label: "Bearing Number", note: "e.g. 6205, 32210, NU210" },
  { icon: Ruler, label: "Bearing Type / Dimensions", note: "Or share existing measurements" },
  { icon: Package, label: "Quantity Required", note: "Any quantity — retail or bulk" },
  { icon: Wrench, label: "Machine / Application", note: "Where the bearing will be used" },
  { icon: Camera, label: "Photograph of Existing Bearing", note: "Optional — helps identify the exact model" },
  { icon: Building2, label: "Delivery Location", note: "For freight and delivery estimate" },
];

const CAPABILITIES = [
  "Bearings sourced from multiple leading brands based on customer requirement",
  "Ball, roller, tapered, spherical, cylindrical, thrust, needle and pillow-block units",
  "Bulk supply for maintenance, overhaul and OEM builds",
  "Technical assistance to identify the right model from a sample or drawing",
  "Delivery across Telangana and Andhra Pradesh",
];

const BEARING_TYPES = [
  "Ball Bearings",
  "Roller Bearings",
  "Taper Roller Bearings",
  "Spherical Roller Bearings",
  "Cylindrical Roller Bearings",
  "Needle Bearings",
  "Thrust Bearings",
  "Pillow Block Bearings",
  "Other Industrial Bearings",
];

export default function BearingsPage() {
  const { openQuote } = useQuote();
  const { settings } = useOutletContext() || { settings: {} };
  const s = settings || {};
  const phone = (s.phone || "").replace(/\s/g, "");
  const wa = (s.whatsapp || "").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${wa}?text=${encodeURIComponent("Hello SSK Enterprises, I would like a quotation for industrial bearings.")}`;

  useSeo({
    title: "Industrial Bearings Supplier in Hyderabad — NTN & Multi-Brand Bearings | SSK Enterprises",
    description:
      "SSK Enterprises supplies industrial bearings across Telangana and Andhra Pradesh, including NTN bearings. Share your bearing number, photo or machine application and get a quotation within one business day.",
    path: "/products/industrial-bearings",
    jsonLd: [breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Products", path: "/products" },
      { name: "Industrial Bearings", path: "/products/industrial-bearings" },
    ])],
  });

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[55vh] flex items-center border-b border-slate-800 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Industrial bearings" loading="eager" fetchpriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220]/95 via-[#0B1220]/85 to-[#0B1220]/60" />
        </div>
        <div className="container-x relative z-10 text-white py-16 md:py-24">
          <div className="flex flex-wrap gap-2 mb-5">
            {Object.values(DIVISIONS).map((d) => (
              <Link key={d.key} to={d.slug === "industrial-bearings" ? "/products/industrial-bearings" : `/products/${d.slug}`}
                className={`px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-wider border transition-colors ${
                  d.slug === "industrial-bearings"
                    ? "bg-[#B5121B] border-[#B5121B] text-white"
                    : "border-slate-700 text-slate-300 hover:border-white hover:text-white"
                }`}>
                {d.short}
              </Link>
            ))}
          </div>
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-6xl font-black leading-tight">Industrial Bearings from Multiple Leading Brands</h1>
            <p className="text-lg md:text-xl text-slate-200 mt-5 leading-relaxed">
              We supply industrial bearings based on customer requirements — sourced from leading global bearing brands.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => openQuote({ division: "bearings" })} className="btn-accent" data-testid="bearing-hero-quote">
                Request a Bearing Quotation <ArrowRight className="w-4 h-4" />
              </button>
              <a href={waUrl} target="_blank" rel="noreferrer" className="btn-ghost-light">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href={`tel:${phone}`} className="btn-ghost-light">
                <Phone className="w-4 h-4" /> Call {s.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BEARING ENQUIRY REQUIREMENTS */}
      <section className="section" data-testid="bearing-enquiry-section">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight">Need a bearing? Send us these details.</h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              To source the exact bearing you need — quickly and at the right price — share whatever details you have. A bearing number or a clear photo of the existing bearing is usually enough for our team to identify and quote.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => openQuote({ division: "bearings" })} className="btn-primary" data-testid="bearing-request-quote">
                Request a Bearing Quotation <ArrowRight className="w-4 h-4" />
              </button>
              <a href={waUrl} target="_blank" rel="noreferrer" className="btn-outline">
                <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ENQUIRY_REQS.map((r, i) => (
              <div key={i} className="tech-card p-5 flex gap-4" data-testid={`bearing-req-${i}`}>
                <div className="w-11 h-11 bg-[#0F2747] text-white flex items-center justify-center flex-shrink-0">
                  <r.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-[#1E293B]">{r.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{r.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES + BEARING TYPES */}
      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-x grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl md:text-3xl font-black text-[#1E293B]">What we can source</h2>
            <ul className="mt-6 space-y-3">
              {CAPABILITIES.map((c) => (
                <li key={c} className="flex gap-3 items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#0F2747] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <button onClick={() => openQuote({ division: "bearings" })} className="btn-accent" data-testid="bearing-types-quote">
                Request a Bearing Quotation <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="tech-card p-7 md:p-9" data-testid="bearing-types-panel">
              <div className="overline">Bearing Types We Supply</div>
              <h3 className="font-display text-xl md:text-2xl font-black text-[#0F2747] leading-tight mt-2">Full range of industrial bearings</h3>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {BEARING_TYPES.map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <Disc3 className="w-4 h-4 text-[#B5121B] flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                    <span className="text-[#1E293B] font-semibold text-[14px] leading-tight">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section bg-[#0F2747] text-white">
        <div className="container-x grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-black">Share your bearing details today</h2>
            <p className="text-white/85 mt-4">Our team will get back with availability, brand options and pricing within one business day.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <button onClick={() => openQuote({ division: "bearings" })} className="btn-accent">Request a Bearing Quotation</button>
            <a href={waUrl} target="_blank" rel="noreferrer" className="btn-ghost-light"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          </div>
        </div>
      </section>
    </>
  );
}
